/**
 * ReservesTab — Flight Board → Buffers (Matt, 2026-10-27; tab renamed from Reserves the same day: Goldratt's word,
 * and Fuel means the design budget only). Keep In the Air and the buffer chart, drop
 * the rest; "the list of projects and their burned reserves … would compliment the fever chart").
 *
 * One read, the Build Ready forecast (api/_buildReady.js via BuildReadyStrip's shared cache), drawn two ways:
 *   1. The fever chart: one dot per project. Right = how much of its plan has posted (the plan's chain less
 *      what's left). Up = how much of its buffer is used. Above the top line is red, between the lines amber.
 *      The lines slope because a project near the end may fairly have spent its cushion; one at the start may not.
 *   2. The launch board (LaunchBoard, the same one the weekly plan opens on): every project, worst first,
 *      with its Build Ready date and buffer days left.
 * Read-only. Replaced the Tower tab's chart, which read the older reserve math (_scheduleTimeline.js).
 */
import { useEffect, useRef, useState } from 'react'
import { loadBuildReady } from './BuildReadyStrip.jsx'
import LaunchBoard from './LaunchBoard.jsx'
import { PALETTE } from './MiniChart.jsx'
import useLiveRefresh from '../lib/useLiveRefresh.js'

const lowerAt = x => 15 + 0.55 * x
const upperAt = x => 45 + 0.55 * x
const ZONE_COLOR = { red: PALETTE.red, amber: PALETTE.amber, green: PALETTE.green }

export default function ReservesTab() {
  const [data, setData] = useState(null)
  const read = () => loadBuildReady(true).then(d => { if (d) setData(d); else setData(x => x || { projects: [] }) }).catch(() => setData(x => x || { projects: [] }))
  useEffect(() => { read() }, [])   // eslint-disable-line react-hooks/exhaustive-deps
  // LIVE: one read feeds the chart and the board below; re-read every minute while in view, after a crew
  // change or a Build Ready change, and when the tab comes back into view (lib/useLiveRefresh.js).
  useLiveRefresh(read, { everyMs: 60000, events: ['crew-index-changed', 'build-ready-changed'], pause: () => !!document.querySelector('[role="dialog"]') })
  const pts = ((data && data.projects) || []).filter(p => p.eligible && !p.reached && p.target && p.burn && p.target.chainDays > 0).map(p => {
    const x = Math.max(0, Math.min(100, Math.round(100 * (1 - (p.chainDays || 0) / p.target.chainDays))))
    const y = Math.round(100 * (p.burn.pct || 0))
    return { id: p.projectId, name: p.name, x, y, late: p.burn.late, lateBy: p.burn.late ? -p.burn.margin : 0, daysLeft: p.burn.daysLeft, buffer: p.target.bufferDays, zone: p.burn.zone === 'red' ? 'red' : p.burn.zone === 'yellow' ? 'amber' : 'green' }   // the server's fever zone, same lines
  })
  return (
    <div style={S.wrap}>
      <div style={S.card}>
        <div style={S.title}>Buffer used</div>
        <div style={S.hint}>One dot per project: right is how much of its plan has posted, up is how much of its buffer is used.</div>
        {data === null ? <div style={S.dim}>Reading every project…</div> : pts.length === 0 ? <div style={S.dim}>No project has a Build Ready plan yet.</div> : <Chart pts={pts} />}
      </div>
      <div style={S.card}>
        <LaunchBoard embedded data={data} onChanged={read} />
      </div>
    </div>
  )
}

const shortName = (n) => String(n || '').replace(/ Residence.*$| Remodel.*$| Second Story.*$/, '').trim()

function Chart({ pts }) {
  const box = useRef(null)
  const [w, setW] = useState(720)
  const [hover, setHover] = useState(null)
  useEffect(() => {
    if (!box.current || typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver(([e]) => setW(Math.max(320, Math.min(880, Math.round(e.contentRect.width)))))
    ro.observe(box.current)
    return () => ro.disconnect()
  }, [])
  // Real pixels: the chart never scales its text or dots with the page.
  const late = pts.filter(p => p.late), live = pts.filter(p => !p.late)
  const L = 40, R = 16, T = late.length ? 40 : 10, B = 30, H = 300 + T
  const pw = w - L - R, ph = H - T - B
  const X = v => L + pw * (Math.max(0, Math.min(100, v)) / 100)
  const Y = v => T + ph * (1 - Math.max(0, Math.min(100, v)) / 100)
  const band = (lo, hi) => `M${X(0)},${Y(lo(0))} L${X(100)},${Y(lo(100))} L${X(100)},${Y(hi(100))} L${X(0)},${Y(hi(0))} Z`
  const mono = '"DM Mono", monospace', sans = '"DM Sans", sans-serif'
  const dot = (p, cx, cy) => (
    <g key={p.id} onMouseEnter={() => setHover({ p, cx, cy })} onMouseLeave={() => setHover(null)} style={{ cursor: 'default' }}>
      <circle cx={cx} cy={cy} r="12" fill="transparent" />
      <circle cx={cx} cy={cy} r={hover && hover.p.id === p.id ? 6.5 : 5} fill={ZONE_COLOR[p.zone]} stroke="var(--aj-bg-surface)" strokeWidth="2" />
    </g>
  )
  return (
    <div ref={box} style={{ position: 'relative', width: '100%', maxWidth: 880, margin: '0 auto' }}>
      <div style={S.legend}>
        {[['red', 'Help now'], ['amber', 'Watch'], ['green', 'On track']].map(([z, t]) => (
          <span key={z} style={S.legendItem}><i style={{ ...S.legendDot, background: ZONE_COLOR[z] }} />{t}</span>
        ))}
      </div>
      <svg width={w} height={H} style={{ display: 'block', overflow: 'visible' }} role="img" aria-label="Buffer used chart">
        {/* Zones, quiet: tint only, labelled at the right edge. */}
        <path d={band(upperAt, () => 100)} fill={PALETTE.red} fillOpacity="0.06" />
        <path d={band(lowerAt, upperAt)} fill={PALETTE.amber} fillOpacity="0.07" />
        <path d={band(() => 0, lowerAt)} fill={PALETTE.green} fillOpacity="0.06" />
        {[25, 50, 75].map(v => <line key={'g' + v} x1={L} x2={L + pw} y1={Y(v)} y2={Y(v)} stroke="var(--aj-border)" strokeWidth="0.5" />)}
        <line x1={L} x2={L + pw} y1={Y(0)} y2={Y(0)} stroke="var(--aj-border-mid)" strokeWidth="0.75" />
        {[0, 50, 100].map(v => <text key={'y' + v} x={L - 8} y={Y(v) + 3.5} textAnchor="end" fontSize="10" fontFamily={mono} fill="var(--aj-text-muted)">{v}%</text>)}
        {[0, 25, 50, 75, 100].map(v => <text key={'x' + v} x={X(v)} y={Y(0) + 16} textAnchor="middle" fontSize="10" fontFamily={mono} fill="var(--aj-text-muted)">{v}%</text>)}
        <text x={L + pw / 2} y={H - 2} textAnchor="middle" fontSize="10" fontFamily={sans} fill="var(--aj-text-muted)">plan posted</text>
        <text x={10} y={T + ph / 2} textAnchor="middle" fontSize="10" fontFamily={sans} fill="var(--aj-text-muted)" transform={`rotate(-90 10 ${T + ph / 2})`}>buffer used</text>
        {/* Past Build Ready: its own lane above the plot, so late projects don't pile on the top edge. */}
        {late.length > 0 && <>
          <line x1={L} x2={L + pw} y1={T - 18} y2={T - 18} stroke={PALETTE.red} strokeOpacity="0.25" strokeWidth="0.75" strokeDasharray="2 3" />
          <text x={L} y={T - 28} fontSize="10" fontFamily={sans} fontWeight="600" fill={PALETTE.red}>Past its Build Ready date</text>
          {late.map(p => dot(p, X(p.x), T - 18))}
        </>}
        {live.map(p => dot(p, X(p.x), Y(p.y)))}
      </svg>
      {hover && (
        <div style={{ ...S.tip, left: Math.min(hover.cx + 12, w - 220), top: Math.max(0, hover.cy - 52) }}>
          <div style={{ fontWeight: 600, color: 'var(--aj-text)' }}>{shortName(hover.p.name)}</div>
          <div>{hover.p.x}% of the plan posted</div>
          <div style={{ color: ZONE_COLOR[hover.p.zone] }}>{hover.p.late ? `Past Build Ready by ${hover.p.lateBy} working days` : `${hover.p.daysLeft} of ${hover.p.buffer} buffer days left`}</div>
        </div>
      )}
    </div>
  )
}

const S = {
  wrap: { display: 'flex', flexDirection: 'column', gap: 16 },
  card: { background: 'var(--aj-bg-surface)', border: '0.5px solid var(--aj-border)', borderRadius: 10, padding: '16px 18px' },
  title: { fontFamily: 'Montserrat, sans-serif', fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--aj-text)' },
  hint: { fontFamily: '"DM Sans", sans-serif', fontSize: 12.5, color: 'var(--aj-text-mid)', margin: '4px 0 10px' },
  legend: { display: 'flex', gap: 16, justifyContent: 'flex-end', marginBottom: 6, fontFamily: '"DM Sans", sans-serif', fontSize: 11.5, color: 'var(--aj-text-mid)' },
  legendItem: { display: 'inline-flex', alignItems: 'center', gap: 6 },
  legendDot: { width: 8, height: 8, borderRadius: 4, display: 'inline-block' },
  tip: { position: 'absolute', pointerEvents: 'none', width: 208, padding: '8px 10px', borderRadius: 8, background: 'var(--aj-bg-elevated)', border: '0.5px solid var(--aj-border-mid)', boxShadow: '0 6px 24px rgba(0,0,0,0.14)', fontFamily: '"DM Sans", sans-serif', fontSize: 12, lineHeight: 1.45, color: 'var(--aj-text-mid)' },
  dim: { fontFamily: '"DM Sans", sans-serif', fontSize: 13, color: 'var(--aj-text-muted)', padding: '12px 0' },
}
